import { RefObject, useEffect } from "react";

/** Gap between the close button and the corner it is pinned to. */
export const CLOSE_BUTTON_INSET_PX = 16;

/**
 * Offsets for a close button absolutely positioned in the top-right corner of
 * `containerRect`. Normally that is a plain inset from the container's own
 * corner, but a form taller or wider than the viewport pushes its corner off
 * screen and takes the close button with it, leaving the shopper unable to
 * dismiss the form. In that case pin the button to the viewport's corner
 * instead, which is still inside the container precisely because the container
 * is the thing overflowing.
 */
export function closeButtonPosition(
  containerRect: { top: number; right: number },
  viewportWidth: number,
  inset: number = CLOSE_BUTTON_INSET_PX,
): { top: string; right: string } {
  const buttonTop = Math.max(containerRect.top + inset, inset);
  const buttonRight = Math.min(
    containerRect.right - inset,
    viewportWidth - inset,
  );
  return {
    top: `${buttonTop - containerRect.top}px`,
    right: `${containerRect.right - buttonRight}px`,
  };
}

/**
 * Keeps the close button on screen while its container overflows the viewport.
 *
 * `closeButtonRef` must be absolutely positioned and `containerRef` must be its
 * offset parent, since the offsets written here are relative to the container.
 */
export function useCloseButtonOnScreen(
  containerRef: RefObject<HTMLElement | null>,
  closeButtonRef: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const reposition = () => {
      const closeButton = closeButtonRef.current;
      if (!closeButton) {
        return;
      }
      const { top, right } = closeButtonPosition(
        container.getBoundingClientRect(),
        window.innerWidth,
      );
      closeButton.style.top = top;
      closeButton.style.right = right;
    };

    const resizeObserver = new ResizeObserver(reposition);
    resizeObserver.observe(container);
    // A container the viewport shrank around, or one a modal shell slides into
    // place after mount, moves relative to the viewport without changing size,
    // so the ResizeObserver alone would leave the button where it started.
    window.addEventListener("resize", reposition);
    window.addEventListener("transitionend", reposition, true);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", reposition);
      window.removeEventListener("transitionend", reposition, true);
    };
  }, [containerRef, closeButtonRef]);
}
